import React, { useEffect, useState, useMemo } from 'react';
import { 
    Container, 
    Paper, 
    Typography, 
    Button,
    Table, 
    TableBody, 
    TableCell, 
    TableContainer, 
    TableHead, 
    TableRow,
    Box,
    Chip,
    TextField,
    InputAdornment,
    TableSortLabel,
    CircularProgress,
    IconButton,
    Tooltip,
    Tabs,
    Tab,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Grid
} from '@mui/material';
import { useNavigate } from 'react-router-dom';
import { 
    ArrowBack, 
    Search, 
    Visibility, 
    AssignmentLate, 
    FiberManualRecord, 
    Assessment, 
    AccountTree, 
    Assignment 
} from '@mui/icons-material';

import TitleBar from '../../components/TitleBar/TitleBar';
import { formatDisplayDate } from '../../utils/dateUtils';
import EntityHierarchyVisualization from '../../components/EntityHierarchyVisualization/EntityHierarchyVisualization';
import { getUser } from '../../services/auth';
import { UserUtils } from '../../types';
import { 
    getMyReviewEntities,
    getReviewEntity,
    getReviewEntityMetricThresholds,
    getRevieweeActiveQuestionnaireResponses,
    getReviewerActiveQuestionnaireResponses,
    getRevieweeClosedQuestionnaireResponses,
    getReviewerClosedQuestionnaireResponses,
    type MetricsThreshold,
    type QuestionnaireResponse,
    type ReviewAttribute
} from '../../services/api';
import EnhancedEntityRaidDialog from '../ViewReviewEntities/EnhancedEntityRaidDialog';
import { AddResponseDialog } from '../QuestionnaireResponses/QuestionnaireResponses';

interface ReviewEntity {
    id: number;
    name: string;
    description: string;
    org_entity_code?: string;
    review_entity_type_id: number;
    reviewers: number[];
    reviewees: number[];
    owner?: number;
    owner_name?: string;
    owner_display_name?: string;
    status?: string;
    valid_from?: string;
    valid_till?: string;
    applicable_review_freqencies: string[];
    created_by: number;
    updated_by: number;
    created_at: string;
    updated_at: string;
    /** Present on JSON API responses; protobuf adapter also sets `review_attributes`. */
    attributes?: ReviewAttribute[];
    review_attributes: Array<{
        id: number;
        name: string;
        description: string;
        value: string;
    }>;
    reviewer_names: string[];
    reviewee_names: string[];
    review_entity_type_name: string;
    review_entity_type?: {
        id: number;
        name: string;
        display_label: string;
        category: string;
        description?: string;
        associated_review_dimensions?: number[];
        associated_review_dimension_names?: string[];
    };
}

type SortField = 'name' | 'description' | 'review_entity_type_name' | 'org_entity_code' | 'category';
type SortDirection = 'asc' | 'desc';

interface TabPanelProps {
    children?: React.ReactNode;
    index: number;
    value: number;
}

function isPercentageMetricValueType(valueType?: string | null): boolean {
    return (valueType ?? '').toLowerCase() === 'percentage';
}

/** Formats a threshold bound; percentage types use fractional decimals (e.g. -0.05 → -5.00%). */
function formatMetricThresholdBound(
    value: number | null | undefined,
    valueType?: string | null
): string {
    if (value === null || value === undefined || Number.isNaN(Number(value))) {
        return 'N/A';
    }
    const n = Number(value);
    if (isPercentageMetricValueType(valueType)) {
        const normalized = Math.abs(n) <= 1 ? n * 100 : n;
        return `${normalized.toFixed(2)}%`;
    }
    return n.toLocaleString(undefined, { maximumFractionDigits: 8 });
}

function effectiveMetricLower(mt: MetricsThreshold): number | null | undefined {
    if (mt.is_overridden && mt.overriden_lower_bound != null) {
        return mt.overriden_lower_bound;
    }
    return mt.org_threshold_lower_bound;
}

function effectiveMetricUpper(mt: MetricsThreshold): number | null | undefined {
    if (mt.is_overridden && mt.overriden_upper_bound != null) {
        return mt.overriden_upper_bound;
    }
    return mt.org_threshold_upper_bound;
}

interface EntityDetailsExtrasTabPanelProps {
    children?: React.ReactNode;
    value: number;
    index: number;
}

function EntityDetailsExtrasTabPanel(props: EntityDetailsExtrasTabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`entity-details-extras-panel-${index}`}
            aria-labelledby={`entity-details-extras-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 2 }}>{children}</Box>}
        </div>
    );
}

function TabPanel(props: TabPanelProps) {
    const { children, value, index, ...other } = props;
    return (
        <div
            role="tabpanel"
            hidden={value !== index}
            id={`review-tabpanel-${index}`}
            aria-labelledby={`review-tab-${index}`}
            {...other}
        >
            {value === index && <Box sx={{ pt: 3 }}>{children}</Box>}
        </div>
    );
}

const ReviewManagement: React.FC = () => {
    const navigate = useNavigate();
    const [tabValue, setTabValue] = useState(0);
    const [userId, setUserId] = useState<number | null>(null);
    const [isReviewer, setIsReviewer] = useState(false);
    const [canCreateReview, setCanCreateReview] = useState(false);
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<string>('');

    // Active Reviews state
    const [activeReviews, setActiveReviews] = useState<QuestionnaireResponse[]>([]);
    const [activeReviewsLoading, setActiveReviewsLoading] = useState(false);

    // Closed Reviews state
    const [closedReviews, setClosedReviews] = useState<QuestionnaireResponse[]>([]);
    const [closedReviewsLoading, setClosedReviewsLoading] = useState(false);

    // Create New Review (Review Entities) state
    const [entities, setEntities] = useState<ReviewEntity[]>([]);
    const [sortField, setSortField] = useState<SortField>('name');
    const [sortDirection, setSortDirection] = useState<SortDirection>('asc');
    const [filterName, setFilterName] = useState<string>('');
    const [filterType, setFilterType] = useState<string>('');
    const [filterDescription, setFilterDescription] = useState<string>('');
    const [filterEntityCode, setFilterEntityCode] = useState<string>('');
    const [filterCategory, setFilterCategory] = useState<string>('');
    const [selectedEntityForView, setSelectedEntityForView] = useState<ReviewEntity | null>(null);
    const [entityDetailsLoading, setEntityDetailsLoading] = useState(false);
    const [viewDialogOpen, setViewDialogOpen] = useState(false);
    const [metricsThresholds, setMetricsThresholds] = useState<MetricsThreshold[]>([]);
    const [metricsLoading, setMetricsLoading] = useState(false);
    /** Review entity details dialog: main tabs (attributes vs metrics) */
    const [entityDetailsExtrasTab, setEntityDetailsExtrasTab] = useState(0);
    /** Sub-tabs within metrics: one tab per threshold_dimension */
    const [metricsDimensionTabIndex, setMetricsDimensionTabIndex] = useState(0);
    const [metricsThresholdSearch, setMetricsThresholdSearch] = useState('');
    const [showHierarchy, setShowHierarchy] = useState(false);
    const [selectedEntityForHierarchy, setSelectedEntityForHierarchy] = useState<ReviewEntity | null>(null);
    const [addReviewDialogOpen, setAddReviewDialogOpen] = useState(false);
    const [selectedEntityForReview, setSelectedEntityForReview] = useState<ReviewEntity | null>(null);
    const [raidDialogOpen, setRaidDialogOpen] = useState(false);
    const [selectedEntity, setSelectedEntity] = useState<ReviewEntity | null>(null);

    // Initialize user
    useEffect(() => {
        const user = getUser();
        if (user?.id) {
            setUserId(user.id);
            // PERFORM_REVIEW role — used for reviewer list APIs only
            setIsReviewer(UserUtils.isReviewer(user));
            // DATA_ENTRY_SUPPORT and REVIEWER cannot create reviews
            setCanCreateReview(UserUtils.canCreateReview(user));
        } else {
            setError('User ID not found');
        }
    }, []);

    // Fetch active reviews
    useEffect(() => {
        if (!userId) return;
        fetchActiveReviews();
    }, [userId, isReviewer]);

    // Fetch entities for Create New Review tab
    useEffect(() => {
        if (!userId || !canCreateReview) return;
        fetchEntities();
    }, [userId, canCreateReview]);

    useEffect(() => {
        // Without create tab: Active(0), Closed(1). Reset only invalid index.
        if (!canCreateReview && tabValue > 1) {
            setTabValue(0);
        }
    }, [canCreateReview, tabValue]);

    const fetchActiveReviews = async () => {
        setActiveReviewsLoading(true);
        try {
            const response = isReviewer 
                ? await getReviewerActiveQuestionnaireResponses()
                : await getRevieweeActiveQuestionnaireResponses();
            setActiveReviews(response.data || []);
        } catch (err) {
            console.error('Error fetching active reviews:', err);
            setActiveReviews([]);
        } finally {
            setActiveReviewsLoading(false);
        }
    };

    const fetchClosedReviews = async () => {
        setClosedReviewsLoading(true);
        try {
            const response = isReviewer 
                ? await getReviewerClosedQuestionnaireResponses()
                : await getRevieweeClosedQuestionnaireResponses();
            setClosedReviews(response.data || []);
        } catch (err) {
            console.error('Error fetching closed reviews:', err);
            setClosedReviews([]);
        } finally {
            setClosedReviewsLoading(false);
        }
    };

    const fetchEntities = async () => {
        setLoading(true);
        try {
            const entityResponse = await getMyReviewEntities();
            setEntities(entityResponse.data as unknown as ReviewEntity[]);
            setError('');
        } catch (err) {
            console.error('Error fetching entities:', err);
            setError('Failed to load review entities');
        } finally {
            setLoading(false);
        }
    };

    const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
        setTabValue(newValue);
        // Fetch data for the selected tab if not already loaded
        const closedTabIndex = canCreateReview ? 2 : 1;
        if (newValue === closedTabIndex && closedReviews.length === 0) {
            fetchClosedReviews();
        }
    };

    const handleViewAnswers = (review: QuestionnaireResponse) => {
        const reviewEntityTypeId = review.review_entity_type_id || 
            (typeof review.review_entity_type_name === 'object' ? review.review_entity_type_name?.id : '');
        navigate(`/review-answers/${review.id}/${reviewEntityTypeId}`);
    };

    const handleStartReview = (entity: ReviewEntity) => {
        if (!canCreateReview) {
            return;
        }
        setSelectedEntityForReview(entity);
        setAddReviewDialogOpen(true);
    };

    const handleAddReviewSuccess = () => {
        setTabValue(0);
        fetchActiveReviews();
    };

    const handleViewDetails = async (entity: ReviewEntity) => {
        setSelectedEntityForView(entity);
        setViewDialogOpen(true);
        setEntityDetailsLoading(true);
        setMetricsLoading(true);
        setEntityDetailsExtrasTab(0);
        setMetricsDimensionTabIndex(0);
        setMetricsThresholdSearch('');

        try {
            const [entityResponse, metricsResponse] = await Promise.allSettled([
                getReviewEntity(entity.id),
                getReviewEntityMetricThresholds(entity.id)
            ]);

            if (entityResponse.status === 'fulfilled') {
                setSelectedEntityForView(entityResponse.value.data as unknown as ReviewEntity);
            }
            if (metricsResponse.status === 'fulfilled') {
                setMetricsThresholds(metricsResponse.value.data);
            } else {
                setMetricsThresholds([]);
            }
        } catch (err) {
            console.error('Error fetching entity details:', err);
        } finally {
            setEntityDetailsLoading(false);
            setMetricsLoading(false);
        }
    };

    const handleCloseViewDialog = () => {
        setViewDialogOpen(false);
        setSelectedEntityForView(null);
        setMetricsThresholds([]);
        setMetricsLoading(false);
        setEntityDetailsLoading(false);
        setEntityDetailsExtrasTab(0);
        setMetricsDimensionTabIndex(0);
        setMetricsThresholdSearch('');
    };

    const handleOpenRaidDialog = (entity: ReviewEntity) => {
        setSelectedEntity(entity);
        setRaidDialogOpen(true);
    };

    const handleViewDetailsFromReview = (review: QuestionnaireResponse) => {
        handleViewDetails({ id: review.review_entity_id, name: review.review_entity_name } as ReviewEntity);
    };

    const handleViewHierarchyFromReview = (review: QuestionnaireResponse) => {
        handleViewHierarchy({ id: review.review_entity_id } as ReviewEntity);
    };

    const handleOpenRaidFromReview = (review: QuestionnaireResponse) => {
        handleOpenRaidDialog({ id: review.review_entity_id, name: review.review_entity_name } as ReviewEntity);
    };

    const handleCloseRaidDialog = () => {
        setRaidDialogOpen(false);
        setSelectedEntity(null);
    };

    const handleViewHierarchy = (entity: ReviewEntity) => {
        setSelectedEntityForHierarchy(entity);
        setShowHierarchy(true);
    };

    const handleCloseHierarchy = () => {
        setShowHierarchy(false);
        setSelectedEntityForHierarchy(null);
    };

    const handleRequestSort = (field: SortField) => {
        const isAsc = sortField === field && sortDirection === 'asc';
        setSortDirection(isAsc ? 'desc' : 'asc');
        setSortField(field);
    };

    const getStatusIcon = (status?: string) => {
        const statusValue = status || 'ACTIVE';
        const color = statusValue === 'ACTIVE' ? 'success' : 
                     statusValue === 'INACTIVE' ? 'error' : 'warning';
        return <FiberManualRecord color={color} fontSize="small" />;
    };

    const renderReviewStatus = (review: QuestionnaireResponse) => {
        const overdueText = review.is_submission_overdue
            ? `Submission overdue${review.days_past_submission_due != null ? ` by ${review.days_past_submission_due}d` : ''}`
            : review.is_closure_overdue
                ? `Closure overdue${review.days_past_closure_due != null ? ` by ${review.days_past_closure_due}d` : ''}`
                : '';
        const overdueDueDate = review.is_submission_overdue
            ? review.submission_due_by
            : review.is_closure_overdue
                ? review.closure_due_by
                : null;

        return (
            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                <Chip
                    label={review.review_status}
                    color={
                        review.review_status === 'SUBMITTED' ? 'success' :
                        review.review_status === 'IN-PROGRESS' ? 'warning' :
                        'default'
                    }
                    size="small"
                />
                {review.is_overdue && (
                    <Chip
                        label={overdueText}
                        color="error"
                        size="small"
                        variant="outlined"
                    />
                )}
                {review.is_overdue && overdueDueDate && (
                    <Typography variant="caption" color="error">
                        Due by {formatDisplayDate(overdueDueDate)}
                    </Typography>
                )}
            </Box>
        );
    };

    const filteredAndSortedEntities = useMemo(() => {
        let filtered = entities.filter(entity => {
            const matchesName = !filterName || entity.name.toLowerCase().includes(filterName.toLowerCase());
            const typeLabel = entity.review_entity_type?.display_label || entity.review_entity_type_name || '';
            const matchesType = !filterType || typeLabel.toLowerCase().includes(filterType.toLowerCase());
            const matchesDescription = !filterDescription || entity.description.toLowerCase().includes(filterDescription.toLowerCase());
            const matchesEntityCode = !filterEntityCode || (entity.org_entity_code && entity.org_entity_code.toLowerCase().includes(filterEntityCode.toLowerCase()));
            const categoryLabel = entity.review_entity_type?.category || '';
            const matchesCategory = !filterCategory || categoryLabel.toLowerCase().includes(filterCategory.toLowerCase());
            return matchesName && matchesType && matchesDescription && matchesEntityCode && matchesCategory;
        });

        return filtered.sort((a, b) => {
            let aValue: string = '';
            let bValue: string = '';
            
            if (sortField === 'category') {
                aValue = a.review_entity_type?.category || '';
                bValue = b.review_entity_type?.category || '';
            } else if (sortField === 'review_entity_type_name') {
                aValue = a.review_entity_type?.display_label || a.review_entity_type_name || '';
                bValue = b.review_entity_type?.display_label || b.review_entity_type_name || '';
            } else {
                aValue = (a[sortField] as string) || '';
                bValue = (b[sortField] as string) || '';
            }
            
            if (sortDirection === 'asc') {
                return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
            } else {
                return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
            }
        });
    }, [entities, filterName, filterType, filterDescription, filterEntityCode, filterCategory, sortField, sortDirection]);

    const viewDialogAttributes = useMemo((): ReviewAttribute[] => {
        if (!selectedEntityForView) return [];
        if (selectedEntityForView.attributes?.length) {
            return selectedEntityForView.attributes;
        }
        const legacy = selectedEntityForView.review_attributes;
        return legacy?.length ? (legacy as unknown as ReviewAttribute[]) : [];
    }, [selectedEntityForView]);

    const metricDimensions = useMemo(() => {
        const dims = new Set<string>();
        metricsThresholds.forEach((mt) => {
            dims.add((mt.threshold_dimension || '').trim() || '—');
        });
        return Array.from(dims).sort((a, b) => a.localeCompare(b));
    }, [metricsThresholds]);

    const thresholdsForSelectedDimension = useMemo(() => {
        if (metricDimensions.length === 0) return [];
        const dim =
            metricDimensions[metricsDimensionTabIndex] ?? metricDimensions[0];
        return metricsThresholds.filter((mt) => {
            const d = (mt.threshold_dimension || '').trim() || '—';
            return d === dim;
        });
    }, [metricsThresholds, metricDimensions, metricsDimensionTabIndex]);

    const displayedMetricThresholdRows = useMemo(() => {
        const q = metricsThresholdSearch.trim().toLowerCase();
        if (!q) return thresholdsForSelectedDimension;
        return thresholdsForSelectedDimension.filter((mt) =>
            (mt.threshold_name || '').toLowerCase().includes(q)
        );
    }, [thresholdsForSelectedDimension, metricsThresholdSearch]);

    useEffect(() => {
        if (metricsDimensionTabIndex >= metricDimensions.length) {
            setMetricsDimensionTabIndex(0);
        }
    }, [metricDimensions.length, metricsDimensionTabIndex]);

    return (
        <>
            <TitleBar title="" />
            <Container maxWidth="xl" sx={{ mt: 4, mb: 4 }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
                    <Typography variant="h4">Review Management</Typography>
                    <Button
                        variant="outlined"
                        startIcon={<ArrowBack />}
                        onClick={() => navigate('/home')}
                    >
                        Back to Home
                    </Button>
                </Box>

                <Paper elevation={3} sx={{ p: 3 }}>
                    <Tabs value={tabValue} onChange={handleTabChange} aria-label="review management tabs">
                        <Tab label="Active Reviews" />
                        {canCreateReview && <Tab label="Create New Review" />}
                        <Tab label="Closed Reviews" />
                    </Tabs>

                    {/* Active Reviews Tab */}
                    <TabPanel value={tabValue} index={0}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            My Active Reviews
                        </Typography>
                            {activeReviewsLoading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                                    <CircularProgress />
                                </Box>
                            )}

                            {!activeReviewsLoading && activeReviews.length === 0 && (
                                <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                    No active reviews found.
                                </Typography>
                            )}

                            {!activeReviewsLoading && activeReviews.length > 0 && (
                                <TableContainer>
                                    <Table>
                                        <TableHead>
                                            <TableRow>
                                                <TableCell><Typography sx={{ fontWeight: 'bold' }}>Review Name</Typography></TableCell>
                                                <TableCell><Typography sx={{ fontWeight: 'bold' }}>Entity</Typography></TableCell>
                                                <TableCell><Typography sx={{ fontWeight: 'bold' }}>Status</Typography></TableCell>
                                                <TableCell><Typography sx={{ fontWeight: 'bold' }}>Review Date</Typography></TableCell>
                                                <TableCell align="center"><Typography sx={{ fontWeight: 'bold' }}>Actions</Typography></TableCell>
                                            </TableRow>
                                        </TableHead>
                                        <TableBody>
                                            {activeReviews.map((review) => (
                                                <TableRow key={review.id}>
                                                    <TableCell>{review.review_name}</TableCell>
                                                    <TableCell>{review.review_entity_name || 'N/A'}</TableCell>
                                                    <TableCell>
                                                        {renderReviewStatus(review)}
                                                    </TableCell>
                                                    <TableCell>{formatDisplayDate(review.review_date)}</TableCell>
                                                    <TableCell align="center">
                                                        <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                                            <Tooltip title="View/Edit Answers">
                                                                <IconButton 
                                                                    size="small" 
                                                                    onClick={() => handleViewAnswers(review)}
                                                                    color="primary"
                                                                >
                                                                    <Assignment />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="View Details">
                                                                <IconButton size="small" onClick={() => handleViewDetailsFromReview(review)}>
                                                                    <Visibility />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="View Hierarchy">
                                                                <IconButton size="small" onClick={() => handleViewHierarchyFromReview(review)} color="primary">
                                                                    <AccountTree />
                                                                </IconButton>
                                                            </Tooltip>
                                                            <Tooltip title="RAID">
                                                                <IconButton size="small" onClick={() => handleOpenRaidFromReview(review)} color="secondary">
                                                                    <AssignmentLate />
                                                                </IconButton>
                                                            </Tooltip>
                                                        </Box>
                                                    </TableCell>
                                                </TableRow>
                                            ))}
                                        </TableBody>
                                    </Table>
                                </TableContainer>
                            )}
                    </TabPanel>

                    {/* Create New Review Tab — hidden for REVIEWER and DATA_ENTRY_SUPPORT */}
                    {canCreateReview && (
                    <TabPanel value={tabValue} index={1}>
                        <Typography variant="h6" sx={{ mb: 2 }}>
                            Review Entities
                        </Typography>
                            <Box sx={{ mb: 3, p: 2, bgcolor: 'info.light', borderRadius: 1 }}>
                                <Typography variant="body2" sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                    <Assessment color="success" />
                                    <strong>Tip:</strong> Click the green <strong>Responses</strong> icon to start a new review for any entity.
                                </Typography>
                            </Box>

                            {loading && (
                                <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                                    <CircularProgress />
                                </Box>
                            )}
                            
                            {error && (
                                <Typography color="error" sx={{ my: 2 }}>
                                    {error}
                                </Typography>
                            )}

                            <TableContainer sx={{ overflowX: 'auto' }}>
                                <Table sx={{ minWidth: 650 }}>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell>
                                                <TextField
                                                    variant="outlined"
                                                    placeholder="Filter by name"
                                                    value={filterName}
                                                    onChange={(e) => setFilterName(e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Search fontSize="small" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    variant="outlined"
                                                    placeholder="Filter by description"
                                                    value={filterDescription}
                                                    onChange={(e) => setFilterDescription(e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Search fontSize="small" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    variant="outlined"
                                                    placeholder="Filter by type"
                                                    value={filterType}
                                                    onChange={(e) => setFilterType(e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Search fontSize="small" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    variant="outlined"
                                                    placeholder="Filter by entity code"
                                                    value={filterEntityCode}
                                                    onChange={(e) => setFilterEntityCode(e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Search fontSize="small" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <TextField
                                                    variant="outlined"
                                                    placeholder="Filter by category"
                                                    value={filterCategory}
                                                    onChange={(e) => setFilterCategory(e.target.value)}
                                                    size="small"
                                                    fullWidth
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Search fontSize="small" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                            </TableCell>
                                            <TableCell align="center"></TableCell>
                                        </TableRow>
                                        <TableRow>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'name'}
                                                    direction={sortField === 'name' ? sortDirection : 'asc'}
                                                    onClick={() => handleRequestSort('name')}
                                                >
                                                    <Typography sx={{ fontWeight: 'bold' }}>Name</Typography>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'description'}
                                                    direction={sortField === 'description' ? sortDirection : 'asc'}
                                                    onClick={() => handleRequestSort('description')}
                                                >
                                                    <Typography sx={{ fontWeight: 'bold' }}>Description</Typography>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'review_entity_type_name'}
                                                    direction={sortField === 'review_entity_type_name' ? sortDirection : 'asc'}
                                                    onClick={() => handleRequestSort('review_entity_type_name')}
                                                >
                                                    <Typography sx={{ fontWeight: 'bold' }}>Type</Typography>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'org_entity_code'}
                                                    direction={sortField === 'org_entity_code' ? sortDirection : 'asc'}
                                                    onClick={() => handleRequestSort('org_entity_code')}
                                                >
                                                    <Typography sx={{ fontWeight: 'bold' }}>Org Entity Code</Typography>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell>
                                                <TableSortLabel
                                                    active={sortField === 'category'}
                                                    direction={sortField === 'category' ? sortDirection : 'asc'}
                                                    onClick={() => handleRequestSort('category')}
                                                >
                                                    <Typography sx={{ fontWeight: 'bold' }}>Category</Typography>
                                                </TableSortLabel>
                                            </TableCell>
                                            <TableCell align="center"><Typography sx={{ fontWeight: 'bold' }}>Actions</Typography></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {filteredAndSortedEntities.map((entity) => (
                                            <TableRow key={entity.id}>
                                                <TableCell>
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                                        <Tooltip title={entity.status || 'ACTIVE'}>
                                                            {getStatusIcon(entity.status)}
                                                        </Tooltip>
                                                        {entity.name}
                                                    </Box>
                                                </TableCell>
                                                <TableCell>{entity.description}</TableCell>
                                                <TableCell>{entity.review_entity_type?.display_label || entity.review_entity_type_name}</TableCell>
                                                <TableCell>{entity.org_entity_code}</TableCell>
                                                <TableCell>{entity.review_entity_type?.category || ''}</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                                        <Tooltip title="View Details">
                                                            <IconButton size="small" onClick={() => handleViewDetails(entity)}>
                                                                <Visibility />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="View Hierarchy">
                                                            <IconButton size="small" onClick={() => handleViewHierarchy(entity)} color="primary">
                                                                <AccountTree />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="Start New Review">
                                                            <IconButton size="small" onClick={() => handleStartReview(entity)} color="success">
                                                                <Assessment />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="RAID">
                                                            <IconButton size="small" onClick={() => handleOpenRaidDialog(entity)} color="secondary">
                                                                <AssignmentLate />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                    </TabPanel>
                    )}

                    {/* Closed Reviews Tab */}
                    <TabPanel value={tabValue} index={canCreateReview ? 2 : 1}>
                        <Typography variant="h6" gutterBottom>
                            Closed Reviews
                        </Typography>
                        
                        {closedReviewsLoading && (
                            <Box sx={{ display: 'flex', justifyContent: 'center', padding: 2 }}>
                                <CircularProgress />
                            </Box>
                        )}

                        {!closedReviewsLoading && closedReviews.length === 0 && (
                            <Typography variant="body2" color="text.secondary" sx={{ my: 2 }}>
                                No closed reviews found.
                            </Typography>
                        )}

                        {!closedReviewsLoading && closedReviews.length > 0 && (
                            <TableContainer>
                                <Table>
                                    <TableHead>
                                        <TableRow>
                                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Review Name</Typography></TableCell>
                                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Entity</Typography></TableCell>
                                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Status</Typography></TableCell>
                                            <TableCell><Typography sx={{ fontWeight: 'bold' }}>Review Date</Typography></TableCell>
                                            <TableCell align="center"><Typography sx={{ fontWeight: 'bold' }}>Actions</Typography></TableCell>
                                        </TableRow>
                                    </TableHead>
                                    <TableBody>
                                        {closedReviews.map((review) => (
                                            <TableRow key={review.id}>
                                                <TableCell>{review.review_name}</TableCell>
                                                <TableCell>{review.review_entity_name || 'N/A'}</TableCell>
                                                <TableCell>
                                                    {renderReviewStatus(review)}
                                                </TableCell>
                                                <TableCell>{formatDisplayDate(review.review_date)}</TableCell>
                                                <TableCell align="center">
                                                    <Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
                                                        <Tooltip title="View Answers">
                                                            <IconButton 
                                                                size="small" 
                                                                onClick={() => handleViewAnswers(review)}
                                                                color="primary"
                                                            >
                                                                <Assignment />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="View Details">
                                                            <IconButton size="small" onClick={() => handleViewDetailsFromReview(review)}>
                                                                <Visibility />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="View Hierarchy">
                                                            <IconButton size="small" onClick={() => handleViewHierarchyFromReview(review)} color="primary">
                                                                <AccountTree />
                                                            </IconButton>
                                                        </Tooltip>
                                                        <Tooltip title="RAID">
                                                            <IconButton size="small" onClick={() => handleOpenRaidFromReview(review)} color="secondary">
                                                                <AssignmentLate />
                                                            </IconButton>
                                                        </Tooltip>
                                                    </Box>
                                                </TableCell>
                                            </TableRow>
                                        ))}
                                    </TableBody>
                                </Table>
                            </TableContainer>
                        )}
                    </TabPanel>
                </Paper>

                {/* View Entity Details Dialog */}
                <Dialog open={viewDialogOpen} onClose={handleCloseViewDialog} maxWidth="lg" fullWidth>
                    <DialogTitle>Review Entity Details</DialogTitle>
                    <DialogContent dividers>
                        {entityDetailsLoading ? (
                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                                <CircularProgress />
                            </Box>
                        ) : selectedEntityForView ? (
                            <Box>
                                <Grid container spacing={2}>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Name</Typography>
                                        <Typography variant="body1">{selectedEntityForView.name}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Entity Code</Typography>
                                        <Typography variant="body1">{selectedEntityForView.org_entity_code || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={12}>
                                        <Typography variant="subtitle2" color="text.secondary">Description</Typography>
                                        <Typography variant="body1">{selectedEntityForView.description || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Type</Typography>
                                        <Typography variant="body1">{selectedEntityForView.review_entity_type?.display_label || selectedEntityForView.review_entity_type_name || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Category</Typography>
                                        <Typography variant="body1">{selectedEntityForView.review_entity_type?.category || 'N/A'}</Typography>
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Status</Typography>
                                        <Chip label={selectedEntityForView.status || 'ACTIVE'} color={selectedEntityForView.status === 'ACTIVE' || !selectedEntityForView.status ? 'success' : 'default'} size="small" />
                                    </Grid>
                                    <Grid item xs={6}>
                                        <Typography variant="subtitle2" color="text.secondary">Owner</Typography>
                                        <Typography variant="body1">{selectedEntityForView.owner_display_name || selectedEntityForView.owner_name || 'N/A'}</Typography>
                                    </Grid>
                                    {selectedEntityForView.reviewer_names && selectedEntityForView.reviewer_names.length > 0 && (
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Reviewers</Typography>
                                            <Typography variant="body1">{selectedEntityForView.reviewer_names.join(', ')}</Typography>
                                        </Grid>
                                    )}
                                    {selectedEntityForView.reviewee_names && selectedEntityForView.reviewee_names.length > 0 && (
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Reviewees</Typography>
                                            <Typography variant="body1">{selectedEntityForView.reviewee_names.join(', ')}</Typography>
                                        </Grid>
                                    )}
                                    {selectedEntityForView.valid_from && (
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Valid From</Typography>
                                            <Typography variant="body1">{formatDisplayDate(selectedEntityForView.valid_from)}</Typography>
                                        </Grid>
                                    )}
                                    {selectedEntityForView.valid_till && (
                                        <Grid item xs={6}>
                                            <Typography variant="subtitle2" color="text.secondary">Valid Till</Typography>
                                            <Typography variant="body1">{formatDisplayDate(selectedEntityForView.valid_till)}</Typography>
                                        </Grid>
                                    )}
                                </Grid>

                                <Box sx={{ mt: 2, pt: 2, borderTop: 1, borderColor: 'divider' }}>
                                    <Tabs
                                        value={entityDetailsExtrasTab}
                                        onChange={(_, v) => setEntityDetailsExtrasTab(v)}
                                        sx={{ borderBottom: 1, borderColor: 'divider' }}
                                        aria-label="Review entity details sections"
                                    >
                                        <Tab
                                            id="entity-details-extras-tab-0"
                                            aria-controls="entity-details-extras-panel-0"
                                            label="Review entity attributes"
                                        />
                                        <Tab
                                            id="entity-details-extras-tab-1"
                                            aria-controls="entity-details-extras-panel-1"
                                            label="Metrics defaults"
                                        />
                                    </Tabs>

                                    <EntityDetailsExtrasTabPanel value={entityDetailsExtrasTab} index={0}>
                                        {viewDialogAttributes.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No custom attributes for this entity.
                                            </Typography>
                                        ) : (
                                            <>
                                                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                                                    Custom fields defined for this review entity (read-only).
                                                </Typography>
                                                <TableContainer component={Paper} variant="outlined">
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell width="22%">
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Name</Typography>
                                                                </TableCell>
                                                                <TableCell width="38%">
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Description</Typography>
                                                                </TableCell>
                                                                <TableCell width="40%">
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Value</Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {viewDialogAttributes.map((attr, index) => (
                                                                <TableRow
                                                                    key={
                                                                        attr.id != null && attr.id !== 0
                                                                            ? attr.id
                                                                            : `${attr.name}-${index}`
                                                                    }
                                                                >
                                                                    <TableCell>{attr.name}</TableCell>
                                                                    <TableCell>{attr.description || '—'}</TableCell>
                                                                    <TableCell>{attr.value ?? '—'}</TableCell>
                                                                </TableRow>
                                                            ))}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </>
                                        )}
                                    </EntityDetailsExtrasTabPanel>

                                    <EntityDetailsExtrasTabPanel value={entityDetailsExtrasTab} index={1}>
                                        {metricsLoading ? (
                                            <Box sx={{ display: 'flex', justifyContent: 'center', p: 2 }}>
                                                <CircularProgress size={24} />
                                            </Box>
                                        ) : metricsThresholds.length === 0 ? (
                                            <Typography variant="body2" color="text.secondary">
                                                No metric thresholds configured.
                                            </Typography>
                                        ) : (
                                            <>
                                                <TextField
                                                    size="small"
                                                    fullWidth
                                                    placeholder="Search by threshold name…"
                                                    value={metricsThresholdSearch}
                                                    onChange={(e) => setMetricsThresholdSearch(e.target.value)}
                                                    sx={{ mb: 2, maxWidth: 420 }}
                                                    InputProps={{
                                                        startAdornment: (
                                                            <InputAdornment position="start">
                                                                <Search fontSize="small" color="action" />
                                                            </InputAdornment>
                                                        ),
                                                    }}
                                                />
                                                <Tabs
                                                    value={metricsDimensionTabIndex}
                                                    onChange={(_, v) => setMetricsDimensionTabIndex(v)}
                                                    variant="scrollable"
                                                    scrollButtons="auto"
                                                    sx={{
                                                        minHeight: 40,
                                                        borderBottom: 1,
                                                        borderColor: 'divider',
                                                        mb: 1,
                                                    }}
                                                    aria-label="Metric thresholds by dimension"
                                                >
                                                    {metricDimensions.map((dim, i) => (
                                                        <Tab key={`${dim}-${i}`} label={dim} />
                                                    ))}
                                                </Tabs>
                                                <TableContainer sx={{ overflowX: 'auto' }}>
                                                    <Table size="small">
                                                        <TableHead>
                                                            <TableRow>
                                                                <TableCell>
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Threshold</Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Dimension</Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Unit of measure</Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Source</Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Lower bound</Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Upper bound</Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Critical</Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Dashboard</Typography>
                                                                </TableCell>
                                                                <TableCell>
                                                                    <Typography sx={{ fontWeight: 'bold' }}>Target</Typography>
                                                                </TableCell>
                                                            </TableRow>
                                                        </TableHead>
                                                        <TableBody>
                                                            {displayedMetricThresholdRows.length === 0 ? (
                                                                <TableRow>
                                                                    <TableCell colSpan={9}>
                                                                        <Typography variant="body2" color="text.secondary">
                                                                            {metricsThresholdSearch.trim()
                                                                                ? 'No thresholds match your search in this dimension.'
                                                                                : 'No thresholds in this dimension.'}
                                                                        </Typography>
                                                                    </TableCell>
                                                                </TableRow>
                                                            ) : (
                                                                displayedMetricThresholdRows.map((mt, idx) => {
                                                                    const vt = mt.value_type;
                                                                    return (
                                                                        <TableRow key={`${mt.org_threshold_id}-${mt.threshold_name}-${idx}`}>
                                                                            <TableCell>{mt.threshold_name}</TableCell>
                                                                            <TableCell>{mt.threshold_dimension}</TableCell>
                                                                            <TableCell>{vt || '—'}</TableCell>
                                                                            <TableCell>
                                                                                {mt.is_overridden ? (
                                                                                    <Chip
                                                                                        label="Overridden"
                                                                                        size="small"
                                                                                        color="warning"
                                                                                        variant="outlined"
                                                                                    />
                                                                                ) : (
                                                                                    <Chip
                                                                                        label="Organization default"
                                                                                        size="small"
                                                                                        color="success"
                                                                                        variant="filled"
                                                                                        sx={{ fontWeight: 500 }}
                                                                                    />
                                                                                )}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {formatMetricThresholdBound(
                                                                                    effectiveMetricLower(mt),
                                                                                    vt
                                                                                )}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {formatMetricThresholdBound(
                                                                                    effectiveMetricUpper(mt),
                                                                                    vt
                                                                                )}
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Chip
                                                                                    label={mt.is_critical ? 'Yes' : 'No'}
                                                                                    size="small"
                                                                                    color={mt.is_critical ? 'error' : 'default'}
                                                                                    variant={mt.is_critical_overridden ? 'filled' : 'outlined'}
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                <Chip
                                                                                    label={(mt.is_dashboard_metric ?? true) ? 'Yes' : 'No'}
                                                                                    size="small"
                                                                                    color={(mt.is_dashboard_metric ?? true) ? 'primary' : 'default'}
                                                                                    variant={mt.is_dashboard_metric_overridden ? 'filled' : 'outlined'}
                                                                                />
                                                                            </TableCell>
                                                                            <TableCell>
                                                                                {mt.target_value != null
                                                                                    ? formatMetricThresholdBound(mt.target_value, vt)
                                                                                    : '—'}
                                                                            </TableCell>
                                                                        </TableRow>
                                                                    );
                                                                })
                                                            )}
                                                        </TableBody>
                                                    </Table>
                                                </TableContainer>
                                            </>
                                        )}
                                    </EntityDetailsExtrasTabPanel>
                                </Box>
                            </Box>
                        ) : null}
                    </DialogContent>
                    <DialogActions>
                        <Button onClick={handleCloseViewDialog}>Close</Button>
                    </DialogActions>
                </Dialog>

                {/* Dialogs */}
                {selectedEntityForReview && (
                    <AddResponseDialog
                        open={addReviewDialogOpen}
                        onClose={() => setAddReviewDialogOpen(false)}
                        reviewEntityId={selectedEntityForReview.id}
                        reviewEntityName={selectedEntityForReview.name}
                        entityReviewerIds={selectedEntityForReview.reviewers}
                        entityRevieweeIds={selectedEntityForReview.reviewees}
                        onSuccess={handleAddReviewSuccess}
                    />
                )}

                {selectedEntity && (
                    <EnhancedEntityRaidDialog
                        open={raidDialogOpen}
                        onClose={handleCloseRaidDialog}
                        entityId={selectedEntity.id}
                        entityName={selectedEntity.name}
                        reviewEntityType={selectedEntity.review_entity_type}
                    />
                )}

                {showHierarchy && selectedEntityForHierarchy && (
                    <EntityHierarchyVisualization
                        entityId={selectedEntityForHierarchy.id}
                        onClose={handleCloseHierarchy}
                    />
                )}
            </Container>
        </>
    );
};

export default ReviewManagement;
